import React, { useCallback, useMemo, useState } from 'react';

import { Text } from '@reckon-web/text';
import { makeId } from '@reckon-web/utils';
import {
  SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRow,
  createDefaultSort,
} from '@reckon-web/table';

import { DueDate } from '../../common/due-date';
import { TaskStatusBadge } from '../../common/task-status-badge';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { Task } from '../../../hooks/use-query-tasks';
import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../../work/types/task-status-enum';
import { WorkItemTitle } from '../../work/helpers';
import { useVisibleProperties } from '../../common/visible-properties';
import { JobStatusEnum } from '../types/job-status-enum';

type ListViewProps = {
  tasks: Task[];
  readonly?: boolean;
  onClickTask?: (taskId: string) => void;
  testId?: string;
};

export const ListView = ({
  tasks,
  readonly,
  onClickTask,
  testId = `list-view`,
}: ListViewProps) => {
  const [{ taskList }] = useVisibleProperties();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'dueDate',
    direction: 'ascending',
  });

  const createSortHandler = useCallback(
    (column: string) => () => {
      setSortDescriptor((prevSort: SortDescriptor) => {
        const isActive = column === prevSort?.column;
        const direction =
          isActive && prevSort?.direction === 'ascending'
            ? 'descending'
            : 'ascending';
        return { column, direction };
      });
    },
    []
  );

  const getSortDirection = useCallback(
    (column: string) => {
      if (sortDescriptor) {
        return sortDescriptor.column === column
          ? sortDescriptor.direction
          : undefined;
      }
    },
    [sortDescriptor]
  );

  const sortedRows = useMemo(() => {
    if (sortDescriptor) {
      return tasks.sort(createDefaultSort(sortDescriptor));
    }
    return tasks;
  }, [tasks, sortDescriptor]);

  const taskStatusesToHideDueDaysFor = new Set<TaskStatusEnumValue>([
    TaskStatusEnum.valuesEnum.Cancelled,
    TaskStatusEnum.valuesEnum.Done,
  ]);

  return (
    <Table
      aria-label="task table list"
      data-testId={makeId(testId, 'data-table')}
    >
      <TableHeader>
        <TableColumnHeader
          onClick={createSortHandler('name')}
          aria-sort={getSortDirection('name')}
        >
          task
        </TableColumnHeader>
        {taskList.client && (
          <TableColumnHeader
            onClick={createSortHandler('entity.name')}
            aria-sort={getSortDirection('entity.name')}
          >
            client
          </TableColumnHeader>
        )}
        {taskList.assigned && (
          <TableColumnHeader
            onClick={createSortHandler('assignee.fullName')}
            aria-sort={getSortDirection('assignee.fullName')}
          >
            assignee
          </TableColumnHeader>
        )}
        {taskList.status && (
          <TableColumnHeader
            onClick={createSortHandler('status')}
            aria-sort={getSortDirection('status')}
          >
            status
          </TableColumnHeader>
        )}
        {taskList.dueDate && (
          <TableColumnHeader
            onClick={createSortHandler('dueDate')}
            aria-sort={getSortDirection('dueDate')}
          >
            due date
          </TableColumnHeader>
        )}
        {taskList.estimatedTime && (
          <TableColumnHeader
            onClick={createSortHandler('estimateHoursDisplay')}
            aria-sort={getSortDirection('estimateHoursDisplay')}
          >
            estimated time
          </TableColumnHeader>
        )}
      </TableHeader>
      <TableBody>
        {sortedRows.map((row) => {
          return (
            <TableRow key={row.cloudId}>
              {/* task */}
              <TableCell>
                <WorkItemTitle
                  name={row.name}
                  locked={
                    row.job?.status.status ===
                      JobStatusEnum.valuesEnum.Closed || readonly
                  }
                  onClick={() => {
                    onClickTask?.(row.cloudId || '');
                  }}
                  testId={makeId(testId, 'title')}
                  weight="regular"
                />
              </TableCell>

              {/* client */}
              {taskList.client && <TableCell>{row.entity?.name}</TableCell>}

              {/* assignee */}
              {taskList.assigned && (
                <TableCell>
                  {row.assignee && (
                    <NamedUserAvatar
                      user={row.assignee}
                      avatarSize="xsmall"
                      textSize="small"
                      testId={makeId(testId, 'avatar')}
                    />
                  )}
                </TableCell>
              )}

              {/* status */}
              {taskList.status && (
                <TableCell>
                  {row.status && (
                    <TaskStatusBadge
                      status={row.status}
                      testId={makeId(testId, 'badge')}
                    />
                  )}
                </TableCell>
              )}

              {/* due date */}
              {taskList.dueDate && (
                <TableCell>
                  {row.dueDate && (
                    <DueDate
                      date={row.dueDate}
                      showDueDays={Boolean(
                        row.status &&
                          !taskStatusesToHideDueDaysFor.has(row.status)
                      )}
                      testId={makeId(testId, 'due')}
                    />
                  )}
                </TableCell>
              )}

              {/* estimated time */}
              {taskList.estimatedTime && (
                <TableCell>
                  {row.estimateHoursDisplay && (
                    <Text
                      size="small"
                      data={{ testid: makeId(testId, 'hours') }}
                    >
                      {row.estimateHoursDisplay} hours
                    </Text>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
